import React, { createContext, useContext, useState, useEffect } from 'react';

const PermissionsContext = createContext();

export const PermissionsProvider = ({ children }) => {
  // Initialize permissions as empty object - no localStorage
  const [permissions, setPermissions] = useState({});

  const setPermissionsFromAPI = (apiPermissions) => {
    // Handle empty object or null permissions (for logout)
    if (!apiPermissions || Object.keys(apiPermissions).length === 0) {
      setPermissions({});
      return;
    }
    
    // If apiPermissions is already an object with boolean values, use it directly
    if (typeof apiPermissions === 'object' && !Array.isArray(apiPermissions)) {
      setPermissions(apiPermissions);
      console.log('Permissions updated from API (object format):', apiPermissions);
      return;
    }
    
    // If it's an array format, convert it to object (fallback for other API formats)
    if (Array.isArray(apiPermissions)) {
      const permissionsMap = apiPermissions.reduce((acc, permission) => {
        acc[permission.slug] = Boolean(permission.is_granted);
        return acc;
      }, {});
      
      setPermissions(permissionsMap);
      console.log('Permissions updated from API (array format):', permissionsMap);
      return;
    }
    
    // Fallback - set empty permissions
    setPermissions({});
  };

  const clearPermissions = () => {
    setPermissions({});
  };

  // Clear permissions when token is removed (logout) - no localStorage cleanup needed
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token' && !e.newValue) {
        setPermissions({});
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <PermissionsContext.Provider value={{ permissions, setPermissionsFromAPI, clearPermissions }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};